// What the thread shows for an item, worked out from the item and the
// athlete's commands still in flight (pure, so it is tested without a
// browser): intake questions and their answers, proposals' outcomes, and
// the coach button's badge: unread coach items plus open proposals (spec
// §13.15 rule 4; coachBadgeCount).
// M5: change cards, the coach log, day dividers, the first unread item, the
// pinned safety card, the morning card and the check-in.

import { COACH_ACTION_TYPES, RENDERED_ITEM_TYPES } from './contract';
import { formatMinutes } from '../lib/duration';

/** An intake question (`question` item) as the thread shows it. */
export function questionView(item, pending = []) {
  const b = item.body || {};
  const sending = pending.find((c) => c.op?.kind === 'intake_answer' && String(c.op.id) === String(item.id));
  const answered = b.answered ?? null;
  const chosen = answered ?? sending?.op.answer ?? null;
  return {
    key: b.question, text: b.text, answers: b.answers || [], freeText: Boolean(b.free_text),
    chosen,
    chosenLabel: (b.answers || []).find((a) => a.id === chosen)?.label || null,
    open: item.state === 'active' && !sending && answered == null,
    sending: Boolean(sending) && item.state === 'active',
  };
}

/** Which answer opens the injury question's few words. */
export const ANSWER_WITH_TEXT = 'yes';
/** The goal answer that waits for a race on the calendar (intake.py). */
export const RACE_ANSWER = 'race';

const OUTCOME = {
  accepted: 'Accepted', dismissed: 'Not now', undone: 'Undone', superseded: 'Replaced by a newer plan',
};

// A proposal the calendar changed under, or past its day (backend
// app/plan/staleness.py): body.stale, its reason, and actions refresh ·
// dismiss. The card says why and offers Refresh instead of Accept.
export const STALE_TEXT = "Your calendar has changed since I suggested this. Refresh and I'll check it still fits.";

/** Why an open proposal can't be accepted as it is, or null. */
export function staleNote(item) {
  const b = item?.body;
  if (!item || item.state !== 'active' || !b?.stale) return null;
  return (typeof b.stale_reason === 'string' && b.stale_reason.trim()) || STALE_TEXT;
}

/** The answers an open proposal offers: refresh · dismiss once stale, else its own (accept · dismiss). */
export function proposalAnswers(item) {
  const listed = Array.isArray(item?.body?.actions) ? item.body.actions : ['accept', 'dismiss'];
  if (staleNote(item)) return ['refresh', ...listed.filter((a) => a === 'dismiss')];
  return listed.filter((a) => a !== 'refresh');
}

/** A proposal's one-line outcome once answered; null while it is open. */
export function proposalOutcome(item) {
  if (!item || item.state === 'active') return null;
  return OUTCOME[item.state] || item.state;
}

/** A coach item the athlete would want to know about: not their own, not a working card, a type the thread draws. */
const badges = (i) => i.type !== 'athlete_message' && i.type !== 'athlete_action' && i.type !== 'working'
  && RENDERED_ITEM_TYPES.includes(i.type);

/**
 * The coach button's badge (mobile lib/v2/threadView.ts coachBadgeCount):
 * the thread items after `readSeq` that aren't the athlete's own, plus the
 * open proposals (each counted once; one being answered doesn't count). A
 * coach_message with actions (plan_empty, plan_failed, ...) counts like any
 * other. Never opened on this device (`readSeq` null): only the open
 * proposals, rather than the whole history.
 */
export function coachBadgeCount(cal, readSeq, pending = []) {
  const acting = new Set(pending.filter((c) => c.op?.kind === 'item_state').map((c) => String(c.op.id)));
  let n = 0;
  for (const i of Object.values(cal?.thread || {})) {
    if (acting.has(String(i.id))) continue;
    const open = i.type === 'proposal' && i.state === 'active';
    const unread = readSeq != null && i.seq > readSeq && badges(i);
    if (open || unread) n += 1;
  }
  return n;
}

/** The browser tab's title with the badge: "(2) aithlo". */
export const badgedTitle = (base, count) => (count > 0 ? `(${count > 99 ? '99+' : count}) ${base}` : base);

// Clear chat (POST /v2/thread/clear): the chat view starts at the last clear. A proposal or question
// still waiting stays, and so does a plan offer whose button still works (OFFER_KINDS; Sam, 2026-11-05:
// Clear chat took "Build my plan" away with nothing planned). Only the chat hides these: other readers of the thread still see every item.
export const isChatCleared = (i) => i.type === 'athlete_action' && i.body?.kind === 'chat_cleared';
export function afterClear(items) {
  const cut = items.reduce((seq, i) => (isChatCleared(i) && i.seq > seq ? i.seq : seq), 0);
  if (!cut) return items;
  const waiting = (i) => (i.state ?? 'active') === 'active' &&
    (i.type === 'proposal' || i.type === 'question' || (i.type === 'coach_message' && OFFER_KINDS.includes(i.body?.kind)));
  return items.filter((i) => i.seq >= cut || waiting(i));
}
export const canClearChat = (items) => items.length > 0 && !isChatCleared(items[items.length - 1]);

/** A strategy proposal (StrategyProposalBody) rather than a session-level one. */
export const isStrategyProposal = (item) => item?.type === 'proposal' && item.body?.kind === 'strategy';

/** The first plan's card has been accepted (onboarding's "See your plan on the calendar"). */
export const strategyAccepted = (items) => (items || []).some((i) => isStrategyProposal(i) && i.state === 'accepted');

/** Set (sessionStorage, to the time in ms) before navigating to a page with the thread: it opens on arrival (onboarding's hand-off). */
export const OPEN_KEY = 'aithlo:openCoach';
/**
 * Set (sessionStorage, to the time in ms) by onboarding's hand-off once
 * /v2/onboarding/complete has started the first plan build: the thread shows
 * the coach on it from arrival (client.expectBuild), before the run's first
 * event, which can take half a minute.
 */
export const BUILD_KEY = 'aithlo:expectBuild';
/**
 * How long the hand-off stands. It is read, not taken: the panel that took it
 * once could be unmounted straight after (the app shell renders nothing while
 * onboarding status reloads on arrival, the panel waits on /v2/me, StrictMode
 * mounts twice), and the next panel has to open too (persona QA, 2026-10-25:
 * the athlete landed on the calendar with the panel closed and both keys
 * gone). It ends when the athlete closes the panel (clearArrival) or after this.
 */
export const ARRIVAL_TTL_MS = 2 * 60 * 1000;

const sessionStore = () => { try { return globalThis.sessionStorage || null; } catch { return null; } };

/**
 * Onboarding's hand-off once /v2/onboarding/complete has answered: the
 * coach thread opens on arrival in the app, and with `build` (the coach-led
 * branch, whose completion starts the first plan) shows the coach on it
 * from that moment.
 */
export function handOffToCoach({ build = true, storage = sessionStore(), now = Date.now() } = {}) {
  try {
    storage?.setItem(OPEN_KEY, String(now));
    if (build) storage?.setItem(BUILD_KEY, String(now));
    else storage?.removeItem(BUILD_KEY);
  } catch { /* the coach button is still there */ }
}

/**
 * What the coach panel does on arrival: `open` (the hand-off asked for it)
 * and `buildSince` (ms: the build to show the coach on, or null). Read, not
 * taken, so every panel mounted while the hand-off stands opens (see
 * ARRIVAL_TTL_MS); a stale one is dropped. `client` null (none yet, or
 * read-only): no build, it is applied once a panel has a client.
 */
export function readArrival(client, storage = sessionStore(), now = Date.now()) {
  try {
    const at = (key) => {
      const raw = storage?.getItem(key);
      if (raw == null) return null;
      const n = Number(raw);
      if (Number.isFinite(n) && n > 1) return n;
      storage.setItem(key, String(now));     // an older hand-off ('1'): it stands from now
      return now;
    };
    const fresh = (t) => t != null && now - t < ARRIVAL_TTL_MS;
    const openAt = at(OPEN_KEY);
    const buildAt = at(BUILD_KEY);
    if (openAt != null && !fresh(openAt)) storage.removeItem(OPEN_KEY);
    if (buildAt != null && !fresh(buildAt)) storage.removeItem(BUILD_KEY);
    return { open: fresh(openAt), buildSince: client && fresh(buildAt) ? buildAt : null };
  } catch {
    return { open: false, buildSince: null };
  }
}

/** The athlete closed the panel: the hand-off is over, a later panel starts closed. */
export function clearArrival(storage = sessionStore()) {
  try {
    storage?.removeItem(OPEN_KEY);
    storage?.removeItem(BUILD_KEY);
  } catch { /* it expires (ARRIVAL_TTL_MS) */ }
}

/**
 * The working item for a plan build whose draft hasn't started yet (the
 * creation view takes over once it has): the run's steps and findings so far
 * (`work`, from run.status / coach.insight), or none while the coach is still
 * getting started.
 */
export function startingView(work = null) {
  return { done: false, steps: work?.steps || [], insights: work?.insights || [], phases: [], sessions: 0,
    startingLabel: STARTING_TEXT };
}
export const STARTING_TEXT = 'Getting started on your plan…';

/** An intake question still open: the build waits on the athlete's answer, so it isn't shown as working. */
export const waitsOnAthlete = (items = [], pending = []) =>
  items.some((i) => i.type === 'question' && questionView(i, pending).open);

/** Set before navigating to the calendar: it opens the race form on arrival (the intake's "a race"). */
export const ADD_RACE_KEY = 'aithlo:addRace';

// ---- M5: the full thread (spec §13.15) ------------------------------------------

const COACH_LOG = new Set(COACH_ACTION_TYPES);

/** How long a change card stays expanded after it was made. */
export const CHANGE_EXPANDED_MS = 24 * 60 * 60 * 1000;
/** The thread counts as quiet (suggested prompts) when its last item is older than this. */
export const QUIET_AFTER_MS = 12 * 60 * 60 * 1000;

export const localDay = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const ms = (iso) => {
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : null;
};

/** The id a change card undoes: refs.change_id (body.change_id on older items). */
export const changeIdOf = (item) => item?.refs?.change_id || item?.body?.change_id || null;

/**
 * A change or session-level proposal as its card shows it: the title, a
 * before → after row per session, the reason, Undo (only while active and
 * undoable) and whether it is expanded: a change for 24 h after it was made,
 * a proposal until it is answered.
 */
export function changeView(item, { now = Date.now(), pending = [] } = {}) {
  const b = item.body || {};
  const isProposal = item.type === 'proposal';
  const acting = pending.some((c) => c.op?.kind === 'item_state' && String(c.op.id) === String(item.id));
  const created = ms(item.created_at);
  const changeId = changeIdOf(item);
  const summary = b.summary || {};
  const counts = [
    summary.sessions_added ? `${summary.sessions_added} added` : null,
    summary.sessions_replaced ? `${summary.sessions_replaced} replaced` : null,
  ].filter(Boolean).join(' · ');
  return {
    title: b.title || (isProposal ? 'A suggestion' : 'Plan updated'),
    reason: b.reason || b.text || null,
    rows: (b.sessions || []).map((r, i) => ({ key: `${r.session_id ?? 'new'}-${i}`, before: r.before || null, after: r.after || null })),
    counts: counts || null,
    outcome: proposalOutcome(item),
    expanded: isProposal ? item.state === 'active' : (created == null || now - created < CHANGE_EXPANDED_MS),
    canUndo: !isProposal && item.state === 'active' && Boolean(b.undoable) && Boolean(changeId) && !acting,
    canAnswer: isProposal && item.state === 'active' && !acting,
    actions: isProposal ? proposalAnswers(item) : b.actions || ['accept', 'dismiss'],
    stale: isProposal ? staleNote(item) : null,
    changeId,
    acting,
  };
}

/** One session on a change card: "Tue 13 Oct · Easy run · run · 45 min". */
export function sessionLine(s) {
  if (!s) return null;
  return [s.day || s.date, s.title, s.sport, s.minutes ? formatMinutes(s.minutes) : null].filter(Boolean).join(' · ');
}

/** All, or the coach log: the coach's actions only. */
export function filterItems(items, filter = 'all') {
  if (filter !== 'coach') return items;
  return items.filter((i) => COACH_LOG.has(i.type));
}

/**
 * The coach log as shown: GET /v2/thread?filter=coach's page (older history)
 * merged with what the stream has carried since (newer states win), oldest first.
 */
export function mergeCoachLog(fetched = [], local = []) {
  const byId = new Map();
  for (const i of fetched) byId.set(String(i.id), i);
  for (const i of local) if (COACH_LOG.has(i.type)) byId.set(String(i.id), i);
  return [...byId.values()].sort((a, b) => (a.seq - b.seq) || String(a.id).localeCompare(String(b.id)));
}

/** "Today", "Yesterday", else "Sat 10 Oct", for the day an item was made. */
export function dayLabel(day, today = localDay()) {
  if (day === today) return 'Today';
  const t = new Date(`${today}T12:00:00`);
  t.setDate(t.getDate() - 1);
  if (day === localDay(t)) return 'Yesterday';
  const d = new Date(`${day}T12:00:00`);
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}

/** The thread's rows: a day divider before the first item of each local day, then the items. */
export function withDayDividers(items, today = localDay()) {
  const rows = [];
  let last = null;
  for (const item of items) {
    const t = ms(item.created_at);
    const day = t == null ? null : localDay(new Date(t));
    if (day && day !== last) {
      rows.push({ kind: 'day', key: `day-${day}`, day, label: dayLabel(day, today) });
      last = day;
    }
    rows.push({ kind: 'item', key: String(item.id), item });
  }
  return rows;
}

/** The first item the athlete hasn't seen (seq past `readSeq`), their own messages aside; null when none. */
export function firstUnreadId(items, readSeq) {
  if (readSeq == null) return null;
  const first = items.find((i) => i.seq > readSeq && i.type !== 'athlete_message' && i.type !== 'athlete_action');
  return first ? String(first.id) : null;
}

/** A safety item stops being pinned this long after it was made (it stays in the history). */
export const SAFETY_PIN_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * The safety card pinned at the top: only the NEWEST active, pinnable safety
 * item, and only while it is unacknowledged on this device and under
 * SAFETY_PIN_MS old. Older ones are never pinned in its place (acknowledging
 * the newest doesn't surface an older duplicate); they stay in the history.
 */
export function pinnedSafety(items, acked = [], now = Date.now()) {
  let newest = null;
  for (let k = items.length - 1; k >= 0; k -= 1) {
    const i = items[k];
    if (i.type === 'safety' && i.state === 'active' && i.body?.pinned !== false) { newest = i; break; }
  }
  if (!newest || acked.map(String).includes(String(newest.id))) return null;
  const made = Date.parse(newest.created_at);
  if (Number.isFinite(made) && now - made > SAFETY_PIN_MS) return null;
  return newest;
}

/** The pinned banner's one line: the first `words` words of the safety text, with … when cut. */
export function safetyPreview(text, words = 8) {
  const all = String(text || '').trim().split(/\s+/).filter(Boolean);
  return all.length > words ? `${all.slice(0, words).join(' ')}…` : all.join(' ');
}

/** Today's morning card: the latest coach_message with body.kind 'morning_card' for `today`. */
export function morningCard(items, today = localDay()) {
  for (let k = items.length - 1; k >= 0; k -= 1) {
    const i = items[k];
    if (i.type === 'coach_message' && i.body?.kind === 'morning_card' && i.body?.day === today) return i;
  }
  return null;
}

// ---- the coach answering (mobile lib/v2/threadView.ts awaitingReply) ------------------
// Sam, 2026-11-05: a message sent, then the old chips came back for a second, then
// "Thinking about your message": between the message landing and the answer's run.status
// arriving nothing said the coach was on it. The wait now shows from the send.

/** The chat answer's run.status label, which the waiting dots say before the run does. */
export const CHAT_RUN_LABEL = 'Thinking about your message…';
/** A message this old with no run working on it isn't getting a reply now (an old thread, a lost run). */
export const GIVE_UP_MS = 5 * 60 * 1000;
const REPLIES = ['coach_message', 'safety', 'proposal', 'change', 'question', 'plan_update'];
const TALK = ['athlete_message', ...REPLIES];

/**
 * Whether the athlete is waiting on the coach's answer: from the send (a message
 * command on its way, or their message the newest thing said) until the answer
 * starts streaming or a reply comes after it. Never read-only, nor while a plan
 * build shows its own progress (`busy`).
 */
export function awaitingReply(items, { live = {}, sending = [], now = Date.now(), readOnly = false, busy = false } = {}) {
  if (readOnly || busy) return false;
  const runs = Object.values(live || {});
  if (runs.some((r) => !!r.text)) return false;           // the answer is streaming: its own bubble shows
  if (sending.length) return true;
  const last = [...items].reverse().find((i) => TALK.includes(i.type));
  if (last?.type !== 'athlete_message') return false;
  const t = Date.parse(last.created_at);
  return runs.length > 0 || !Number.isFinite(t) || now - t <= GIVE_UP_MS;
}

/** The thread is quiet: nothing in it, or nothing for QUIET_AFTER_MS, and the coach isn't working. */
export function isQuiet(items, { now = Date.now(), working = false, sending = false } = {}) {
  if (working || sending) return false;
  const last = items[items.length - 1];
  if (!last) return true;
  const t = ms(last.created_at);
  return t != null && now - t > QUIET_AFTER_MS;
}

// A session opened from the coach (a reply's source or card): the calendar reads
// the key when it mounts, or hears the event when it's already there.
export const OPEN_SESSION_KEY = 'aithlo:v2:openSession';
export const OPEN_SESSION_EVENT = 'calendar:open-session';

/** What the composer's context chip says for an item or a session the athlete asked Why? about. */
export function contextLabel(target) {
  if (!target) return null;
  if (target.type) {
    const b = target.body || {};
    return b.title || (target.type === 'coach_message' ? 'this message' : 'this card');
  }
  return target.title || 'this session';
}

/** The header's status line: the label of a run that is working (`run.status`), else null. */
export function statusLine(live = {}) {
  const runs = Object.values(live || {});
  for (let k = runs.length - 1; k >= 0; k -= 1) if (runs[k].label) return runs[k].label;
  return null;
}

/**
 * GET /v2/sessions/{id} → the session detail's "why": labelled lines for what
 * is there (purpose, why now, role in the week, the week's objective, the
 * phase and its rationale), and the context_ref Ask why sends (the session id
 * as a string). Null when there's nothing to say.
 */
export function sessionWhyView(read) {
  if (!read?.session) return null;
  const intent = read.intent || read.session.intent || {};
  const week = read.week || null;
  const phase = read.phase || null;
  const lines = [
    ['Purpose', intent.purpose],
    ['Why now', intent.why_now],
    ['Role in the week', intent.role_in_week],
    ["This week's objective", week?.objective],
    [phase?.name ? `${phase.name} phase` : 'Phase', phase?.rationale],
  ].filter(([, v]) => v != null && String(v).trim() !== '');
  if (!lines.length) return null;
  return { ref: String(read.session.id), title: read.session.title || intent.purpose || 'this session', lines };
}

/**
 * The human-coach badge for a v2 athlete (aithlo-backend#167): GET /v2/me's
 * `coaching_unread` (as of its last_seq), unless the stream has since carried
 * a newer `coaching.unread`. Same rule as the mobile app's threadView.ts.
 */
export function coachingUnread(cal, me) {
  const fromMe = me?.coaching_unread ?? null;
  if (cal?.coaching_unread_seq == null) return fromMe ?? cal?.coaching_unread ?? 0;
  if (me && me.last_seq != null && cal.coaching_unread_seq <= me.last_seq && fromMe != null) return fromMe;
  return cal.coaching_unread ?? 0;
}

// The coach's plan offers (backend keep_written.OFFER_KINDS): only the latest
// stands, so a retired one isn't shown at all (Sam, 2026-10-26: "I couldn't
// finish your new plan… Try again" above "Nothing's planned… Build my plan").
export const OFFER_KINDS = ['plan_empty', 'plan_failed', 'plan_not_applied', 'proposal_updating', 'fill_failed', 'plan_paused'];

export function isRetiredOffer(item) {
  return item?.type === 'coach_message' && item.state === 'superseded' && OFFER_KINDS.includes(item.body?.kind);
}

// ---- Decisions waiting on the athlete (Sam, 2026-10-29: "approval cards get lost up the chat") ----

/**
 * The Needs-you block at the BOTTOM of the thread (Sam, 2026-10-29: the
 * decision sits where the conversation is, not above weeks of history): the
 * coach's open proposals (strategy, block/week rework, session-level) and the
 * intake questions still open, in thread order (newest decision last). They
 * render there only, out of their chronological place; once answered
 * (accepted, not now, an answer on its way) they're back in the history.
 */
export const isDecision = (item, pending = []) => (item?.type === 'proposal' && item.state === 'active')
  || (item?.type === 'question' && questionView(item, pending).open);
export const needsYouItems = (items = [], pending = []) => items.filter((i) => isDecision(i, pending));

/**
 * Everything the athlete still has to act on, in the order the Needs-you block
 * shows it (its own selection, so the bar and the block can't disagree): a
 * proposal whose Accept / Not now is on its way doesn't wait any more (it stays
 * in the block, busy, until its new state lands). A coach message with an
 * action (Try again, Build my plan) is an offer, not a decision the coach waits
 * on (coachBadgeCount counts it as unread only), so it isn't here.
 */
export function waitingItems(items = [], pending = []) {
  const acting = new Set(pending.filter((c) => c.op?.kind === 'item_state').map((c) => String(c.op.id)));
  return needsYouItems(items, pending).filter((i) => !acting.has(String(i.id)));
}

/** A waiting card's title as the bar names it. */
export function waitingTitle(item) {
  const b = item?.body || {};
  if (item?.type === 'question') return b.text || 'A question from your coach';
  if (isStrategyProposal(item)) return b.title || 'Proposed plan';
  return b.title || 'A suggestion';
}

/**
 * The bar docked above the composer while the Needs-you block is scrolled out
 * of view: "Waiting for you · <title> · Review ↓" for one card, "<n> things
 * waiting for you · Review ↓" for several; null when none.
 */
export function waitingBar(waiting = []) {
  if (!waiting.length) return null;
  if (waiting.length === 1) {
    const title = waitingTitle(waiting[0]);
    return { lead: 'Waiting for you', title, label: `Waiting for you: ${title}. Review it below` };
  }
  const lead = `${waiting.length} things waiting for you`;
  return { lead, title: null, label: `${lead}. Review the first below` };
}

/**
 * Whether the Needs-you block counts as in view (an IntersectionObserver entry
 * against the thread list): enough of it shows to read it (120px, or 60% of a
 * shorter block). While it is, the bar has nothing to point at and hides.
 */
export function blockInView(entry) {
  if (!entry?.isIntersecting) return false;
  const seen = entry.intersectionRect?.height ?? 0;
  const full = entry.boundingClientRect?.height ?? 0;
  return seen > 0 && seen >= Math.min(120, full * 0.6);
}

/** Opens the coach panel on a card (or the first card waiting, with no id): scrolled to and highlighted. */
export const REVIEW_EVENT = 'chat:review';
export function reviewInThread(itemId = null, win = globalThis.window) {
  win?.dispatchEvent?.(new CustomEvent(REVIEW_EVENT, { detail: itemId == null ? {} : { item_id: String(itemId) } }));
}

/** How long a card reviewed from the bar (or a link) stays highlighted. */
export const HIGHLIGHT_MS = 1500;
export const HIGHLIGHT_CLASSES = ['ring-2', 'ring-accent-default', 'ring-offset-2', 'ring-offset-bg-panel', 'rounded-card'];

export const prefersReducedMotion = (win = globalThis.window) => {
  try { return Boolean(win?.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches); } catch { return false; }
};

/**
 * Scrolls the thread item `id` inside `root` into view (smoothly unless the
 * athlete prefers reduced motion) and, with `highlight`, rings it for
 * HIGHLIGHT_MS. Returns the element, or null when it isn't rendered.
 */
export function focusThreadItem(root, id, { smooth = true, highlight = true, win = globalThis.window, ms: dur = HIGHLIGHT_MS } = {}) {
  if (!root || id == null) return null;
  const el = [...root.querySelectorAll('[data-item-id]')].find((e) => e.getAttribute('data-item-id') === String(id));
  if (!el) return null;
  el.scrollIntoView?.({ block: 'start', behavior: smooth && !prefersReducedMotion(win) ? 'smooth' : 'auto' });
  if (highlight) {
    el.classList.add(...HIGHLIGHT_CLASSES);
    el.setAttribute('data-highlighted', 'true');
    clearTimeout(el.__aithloHighlight);
    el.__aithloHighlight = setTimeout(() => {
      el.classList.remove(...HIGHLIGHT_CLASSES);
      el.removeAttribute('data-highlighted');
    }, dur);
  }
  return el;
}
